import { getMarqueeCardImageUrl } from '../marqueeCardImage';

type MarqueeItem = Readonly<{
	cardType: string;
	usePetitionPhoto: boolean;
	featuredComment: Readonly<{ textStripped: string }> | null;
	featuredUpdate: Readonly<{ __typename: string; descriptionStripped: string; content?: string | null }> | null;
	petition: Readonly<{
		photo: Readonly<{ petitionSquare: Readonly<{ url: string }> }> | null;
		user: Readonly<{ photo: Readonly<{ userMedium: Readonly<{ url: string }> }> | null }>;
		signatureState: Readonly<{ signatureGoal: Readonly<{ displayed: number }> }>;
	}>;
}>;

/**
 * The concrete update kind - not the entry's cardType - is what proves which fields exist:
 * a pinned update of the wrong kind must drop the card, not render another kind's fields.
 */
function hasUpdateOfKind(item: MarqueeItem, typename: string): boolean {
	return item.featuredUpdate?.__typename === typename;
}

// An image is required of every entry by canBuildMarqueeCard below rather than per shape here,
// which is why the two shapes that render one carry no image check - see the note there.
const CARD_TYPE_REQUIREMENTS: Readonly<Partial<Record<string, (item: MarqueeItem) => boolean>>> = {
	PETITION: () => true,
	MILESTONE: (item) => hasUpdateOfKind(item, 'MilestonePetitionUpdate'),
	SUPPORTER_VOICE: (item) => !!item.featuredComment?.textStripped,
	STARTER_UPDATE: (item) =>
		hasUpdateOfKind(item, 'StarterPetitionUpdate') && !!item.featuredUpdate?.descriptionStripped,
	// a DM response's body is its description, like a starter update's
	DECISION_MAKER: (item) =>
		hasUpdateOfKind(item, 'DmResponsePetitionUpdate') && !!item.featuredUpdate?.descriptionStripped,
	// an endorsement's text is `content`; its descriptionStripped is always empty
	ENDORSEMENT: (item) => hasUpdateOfKind(item, 'EndorsementPetitionUpdate') && !!item.featuredUpdate?.content,
	MOMENTUM: () => true,
	CLOSE_TO_GOAL: (item) => item.petition.signatureState.signatureGoal.displayed > 0,
};

/**
 * Whether a curated marquee entry has the data its card shape renders. This is the single
 * definition of "renderable": getHomepagePageData filters on it so shouldShowPetitionMarquee
 * only reports true when at least one card will actually appear (otherwise the hero picks
 * the marquee over its impact-stories fallback and renders an empty slot), and
 * usePetitionMarquee's buildCard trusts it before narrowing.
 *
 * WHILE THE SIMPLE CARD TEST RUNS: an image is required of every card type, not just the two
 * shapes that show one. social_post renders every curated entry as the simple petition card
 * while social_post_new renders the curated shapes, so without this an image-less quote or
 * momentum entry would render in one arm and be dropped in the other. That would leave the
 * arms with different card counts, and selectMarqueeRows sizes its rows from the list length,
 * so the two arms would scroll at visibly different speeds. When the test concludes, move the
 * image check back onto PETITION and MILESTONE alone.
 */
export function canBuildMarqueeCard(item: MarqueeItem): boolean {
	if (!getMarqueeCardImageUrl(item)) return false;

	return CARD_TYPE_REQUIREMENTS[item.cardType]?.(item) ?? false;
}
